import { ScheduleContent } from 'components/Wizards/ContentPlanning/ScheduleContent/ScheduleContent'
import { Header as ContentPlanningHeader } from 'components/Wizards/ContentPlanning/Header/Header'

import { PlanContentProps } from './Types'
import { useContentPlanningContext } from '../ContentPlanningContext'
import { postCountToPostingCadence, scheduleContent } from 'components/Wizards/utils/postScheduling'
import { PostCount } from 'components/Wizards/schemas/shared/captionCount'
import dayjs, { Dayjs } from 'dayjs'
import { Formik } from 'formik'
import { AlmanacFile } from 'types'
import { DayAndTime } from 'components/Wizards/ContentPlanning/ScheduleContent/components/StartDateAndPostFrequency/StartDateAndPostFrequencyModal'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { useBrandVisualKits } from 'hooks/queries/workspaces/useBrandVisualKits'
import { StepSchema } from 'components/Wizards/schemas/Types'

type Values = {
  project: AlmanacFile | undefined
  filename: string | undefined
  external_integration_id: number | undefined
}

const PlanContent = multiStep<PlanContentProps>(
  ({ onBack, onSubmit, externalIntegrationType, header, subHeader, previousLabel, ...props }) => {
    const { setScheduledCaptions, scheduledCaptions, selectedCaptions } = useContentPlanningContext()
    const initialStartDate = dayjs().minute(0).second(0).add(1, 'day')

    const handleScheduleChange = (schedule: { daysAndTimes: DayAndTime[]; startDate: Dayjs }) => {
      const captionsToSchedule = scheduledCaptions.filter((c) => selectedCaptions.includes(c.uuid))
      const { daysAndTimes, startDate } = schedule
      const captions = scheduleContent(captionsToSchedule, daysAndTimes, startDate)

      setScheduledCaptions((currentCaptions) => {
        return currentCaptions.map((caption) => {
          const newCaption = captions.find((c) => c.uuid === caption.uuid)
          if (!newCaption) return caption

          return {
            ...caption,
            postAt: newCaption.postAt,
          }
        })
      })
    }

    const goBack = () => {
      onBack?.()
    }

    const hasTemplates = scheduledCaptions.some((content) => Boolean(content.sourceVisualElementId))

    const schema = hasTemplates ? props.schema : props.schema.omit(['apply_brand', 'brand_visual_kit_id'] as any)

    const brandVisualKits = useBrandVisualKits().data?.brand_visual_kits || []
    const lastBrandVisualKit = brandVisualKits[brandVisualKits.length - 1]

    const initialValues = {
      ...schema.getDefault(),
      ...props.initialValues,
      apply_brand: true,
      brand_visual_kit_id: props.initialValues?.brand_visual_kit_id ?? lastBrandVisualKit?.id,
    }

    return (
      <Formik
        enableReinitialize
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={schema.cast(initialValues, { assert: false })}
      >
        {({ values, setFieldValue, submitForm }) => (
          <ScheduleContent
            header={<ContentPlanningHeader header={header} subHeader={subHeader} />}
            inputPlaceholder="Write your own caption..."
            scheduledContent={scheduledCaptions}
            setScheduledContent={setScheduledCaptions}
            externalIntegrationType={externalIntegrationType}
            onSubmit={submitForm}
            goBack={goBack}
            initialPostingCadence={postCountToPostingCadence(initialValues.caption_count as PostCount)}
            onScheduleChange={handleScheduleChange}
            setCampaignName={(filename) => setFieldValue('filename', filename)}
            onSelectedFolder={(project) => setFieldValue('project', project)}
            selectedFolder={(values as Values).project}
            previousLabel={previousLabel}
            campaignName={(values as Values).filename}
            lineClamp={3}
            initialStartDate={initialStartDate}
            schema={schema as StepSchema}
            externalIntegrationId={(values as Values).external_integration_id}
            onSelectExternalIntegrationId={(external_integration_id) =>
              setFieldValue('external_integration_id', external_integration_id)
            }
          />
        )}
      </Formik>
    )
  },
)

export { PlanContent }
